const API_URL = '/waste-customer'

export const state = () => ({
  waste_customers: [],
  waste_collection_history: [],
  waste_collection_routes: [],
  contracts: { items: [] },
  chart_data: [],
  chart_data_filter_options: [],
  subjects: { items: [] },
})

export const getters = {
  get: state => waste_customer_id => {
    return state.waste_customers.find(waste_customer => waste_customer.id === parseInt(waste_customer_id))
  },
  getChartFilterOptions: state => {
    return state.chart_data_filter_options
  },
}

export const mutations = {
  PUSH_OR_REPLACE_WASTE_CUSTOMER(state, new_waste_customer) {
    if (!new_waste_customer || !new_waste_customer?.id) {
      return
    }

    let waste_customer_index = state.waste_customers.findIndex(
      waste_customer => waste_customer.id === parseInt(new_waste_customer.id)
    )

    if (waste_customer_index >= 0) {
      state.waste_customers.splice(waste_customer_index, 1, new_waste_customer)
    } else {
      state.waste_customers.push(new_waste_customer)
    }
  },
  REMOVE_WASTE_CUSTOMER(state, waste_customer_id) {
    let waste_customer_index = state.waste_customers.findIndex(
      waste_customer => waste_customer.id === parseInt(waste_customer_id)
    )

    state.waste_customers.splice(waste_customer_index, 1)
  },
  PUSH_OR_REPLACE_WASTE_COLLECTION_HISTORY(state, history) {
    state.waste_collection_history = history
  },
  PUSH_OR_REPLACE_WASTE_COLLECTION_ROUTES(state, routes) {
    state.waste_collection_routes = routes
  },
  PUSH_OR_REPLACE_CONTRACTS(state, contracts) {
    state.contracts = contracts
  },
  PUSH_OR_REPLACE_CHART_DATA(state, data) {
    state.chart_data = data
  },
  PUSH_OR_REPLACE_CHART_FILTER_OPTIONS(state, filterOptions) {
    state.chart_data_filter_options = filterOptions
  },
  PUSH_OR_REPLACE_WASTE_CUSTOMER_CONTRACT(state, new_waste_customer_contract) {
    let waste_customer_contract_index = state.contracts.items.findIndex(
      waste_customer_contract => waste_customer_contract.id === parseInt(new_waste_customer_contract.id)
    )

    if (waste_customer_contract_index >= 0) {
      state.contracts.items.splice(waste_customer_contract_index, 1, new_waste_customer_contract)
    } else {
      state.contracts.items.push(new_waste_customer_contract)
    }
  },
  PUSH_OR_REPLACE_SUBJECTS(state, subjects) {
    state.subjects = subjects
  },
  PUSH_OR_REPLACE_SUBJECT(state, new_subject) {
    let subject_index = state.subjects.items.findIndex(subject => subject.id === parseInt(new_subject.id))

    if (subject_index >= 0) {
      state.subjects.items.splice(subject_index, 1, new_subject)
    } else {
      state.subjects.items.push(new_subject)
    }
  },
}

export const actions = {
  async fetch({ commit }, waste_customer_id) {
    let { data: waste_customer } = await useApi(API_URL + '/' + waste_customer_id)

    commit('PUSH_OR_REPLACE_WASTE_CUSTOMER', waste_customer.value)

    return waste_customer.value
  },
  async update({ commit }, { waste_customer, waste_customer_id }) {
    let { data } = await useApi(API_URL + '/' + waste_customer_id, {
      method: 'put',
      body: waste_customer,
    })
    waste_customer = data.value
    commit('PUSH_OR_REPLACE_WASTE_CUSTOMER', waste_customer)

    return waste_customer
  },
  async delete({ commit }, waste_customer_id) {
    await useApi(API_URL + '/' + waste_customer_id, { method: 'delete' })

    commit('REMOVE_WASTE_CUSTOMER', waste_customer_id)
  },
  async fetchWasteCollectionHistory({ commit }, { waste_customer_id, params }) {
    let { data: wasteCollectionHistory } = await useApi(`${API_URL}/${waste_customer_id}/waste-collection`, {
      query: params,
    })

    commit('PUSH_OR_REPLACE_WASTE_COLLECTION_HISTORY', wasteCollectionHistory.value)

    return wasteCollectionHistory.value
  },
  async fetchWasteCollectionRoutes({ commit }, { waste_customer_id, params }) {
    let { data: wasteCollectionRoutes } = await useApi(`${API_URL}/${waste_customer_id}/waste-collection-route`, {
      query: params,
    })

    commit('PUSH_OR_REPLACE_WASTE_COLLECTION_ROUTES', wasteCollectionRoutes.value)

    return wasteCollectionRoutes.value
  },
  async fetchWasteCollectionRoutesForBranch({ commit }, { waste_customer_id, waste_company_branch_id, params }) {
    let { data: wasteCollectionRoutes } = await useApi(
      `${API_URL}/${waste_customer_id}/waste-collection-route/${waste_company_branch_id}`,
      {
        query: params,
      }
    )

    commit('PUSH_OR_REPLACE_WASTE_COLLECTION_ROUTES', wasteCollectionRoutes.value)

    return wasteCollectionRoutes.value
  },
  async fetchContracts({ commit }, { waste_customer_id, waste_company_branch_id }) {
    let { data: contracts } = await useApi(
      `${API_URL}/${waste_customer_id}/waste-customer-contract/${waste_company_branch_id}`
    )

    commit('PUSH_OR_REPLACE_CONTRACTS', contracts.value)

    return contracts.value
  },
  async fetchChartData({ commit }, { waste_customer_id, params }) {
    let { data: chartData } = await useApi(`${API_URL}/${waste_customer_id}/waste-evidence`, { query: params })

    commit('PUSH_OR_REPLACE_CHART_DATA', chartData.value)

    return chartData.value
  },
  async fetchChartFilterOptions({ commit }, waste_customer_id) {
    let { data: filterOptions } = await useApi(`${API_URL}/${waste_customer_id}/waste-evidence/filter-options`)

    commit('PUSH_OR_REPLACE_CHART_FILTER_OPTIONS', filterOptions.value)

    return filterOptions.value
  },
  async storeWasteCustomerContract(
    { commit },
    { waste_customer_id, waste_company_branch_id, waste_customer_contract }
  ) {
    let { data: data, serverError: error } = await useApi(
      `${API_URL}/${waste_customer_id}/waste-customer-contract/${waste_company_branch_id}`,
      {
        method: 'post',
        body: waste_customer_contract,
      }
    )

    if (serverError.value) {
      throw new Error(`Failed to store waste customer contract: ${error.value}`)
    }

    let newWasteCustomerContract = JSON.parse(JSON.stringify(waste_customer_contract))
    newWasteCustomerContract.id = data.value

    commit('PUSH_OR_REPLACE_WASTE_CUSTOMER_CONTRACT', newWasteCustomerContract)

    return newWasteCustomerContract
  },
  async fetchSubjects({ commit }, { waste_customer_id, params }) {
    let { data: subjects } = await useApi(`${API_URL}/${waste_customer_id}/subject`, { query: params })

    commit('PUSH_OR_REPLACE_SUBJECTS', subjects.value)

    return subjects.value
  },
  async storeSubject({ commit }, { waste_customer_id, subject }) {
    let { data, error } = await useApi(`${API_URL}/${waste_customer_id}/subject`, {
      method: 'post',
      body: subject,
    })

    if (error.value) {
      console.log('>>', error.value)
      throw error.value
    }

    subject.id = data.value

    commit('PUSH_OR_REPLACE_SUBJECT', subject)

    return subject
  },
}
